import React from "react";
import { Grid, Box } from "@mui/material";
import Flx from "../../layout/Flx";
import Txt from "../../typography/Txt";
import colors from "../../../theme/colors";
import FieldHint from "./FieldHint";
import { useRffFieldStyle } from "./rffFieldStyleContext";
import { useFormGrid } from "./formGridContext";

/**
 * RffInputWrapper — owns label rendering, the required asterisk, helper text,
 * and grid sizing for every Rff* field. No exceptions: every Rff field wraps
 * its <Field>-rendered input in this component.
 *
 * Props:
 *   label, required, helperText — label-area content
 *   hint — text for a "?" tooltip beside the label, for the explanation that is
 *     too long to sit under the control on every row
 *   optional — render an "Optional" tag beside the label
 *   row — render label inline to the left of the input (default: label above input)
 *   size — MUI Grid item size (xs=12 sm=size) when wrapped in a Grid container.
 *     Also accepts a breakpoint map ({ sm: 6, lg: 4 }) for surfaces whose column
 *     count changes with the viewport.
 *   suppressGrid — when true, skip the <Grid item> wrapper (caller is handling layout)
 */
const RffInputWrapper = ({
  label,
  required = false,
  helperText,
  hint,
  optional = false,
  row = false,
  size = 12,
  suppressGrid = false,
  children,
}) => {
  const { filled } = useRffFieldStyle();
  const { active: inFormGrid, columns } = useFormGrid();

  // "filled" variant uses a compact uppercase eyebrow label; default is the
  // standard sentence-case body label.
  const labelSx = filled
    ? {
        fontSize: 11,
        fontWeight: 500,
        letterSpacing: "0.04em",
        textTransform: "uppercase",
        color: colors.slate500,
        minWidth: row ? 160 : undefined,
      }
    : {
        fontWeight: 500,
        color: colors.slate700,
        minWidth: row ? 160 : undefined,
      };

  // The class names are the seam that lets a surrounding surface restyle every
  // field at once without each Rff* growing a prop for it — see
  // PUBLIC_FORM_FIELD_SX, which the public forms spread onto their card to match
  // the LOS field metrics. Targeting bare `label` instead would also catch
  // RffBooleanCheckbox's FormControlLabel.
  const inner = (
    <Flx
      className="rff-field-root"
      column={!row}
      gap={row ? 1.5 : 0.5}
      centerY={row}
      sx={{ width: "100%" }}
    >
      {(label || hint || optional) && (
        // The label, its Optional tag and its hint are siblings on one row —
        // the hint must not live inside the <label>, or clicking it would focus
        // the control instead of just explaining it.
        <Flx centerY gap={0.75} sx={{ minWidth: 0 }}>
          {label && (
            <Txt
              className="rff-field-label"
              component="label"
              variant="body2"
              sx={labelSx}
            >
              {label}
              {required && (
                <Txt
                  className="rff-field-required"
                  component="span"
                  sx={{ color: filled ? colors.danger : "primary.main", ml: 0.5 }}
                >
                  *
                </Txt>
              )}
            </Txt>
          )}
          {optional && (
            <Box
              component="span"
              sx={{
                flex: "none",
                fontSize: 11,
                fontWeight: 600,
                letterSpacing: "0.02em",
                color: colors.slate400,
                backgroundColor: colors.slate50,
                border: `1px solid ${colors.slate200}`,
                borderRadius: "5px",
                px: 0.75,
                py: "1.5px",
              }}
            >
              Optional
            </Box>
          )}
          <FieldHint text={hint} />
        </Flx>
      )}
      <Flx column gap={0.5} sx={{ flex: 1, minWidth: 0 }}>
        {children}
        {helperText && (
          <Txt
            className="rff-field-helper"
            variant="caption"
            color="text.secondary"
          >
            {helperText}
          </Txt>
        )}
      </Flx>
    </Flx>
  );

  if (suppressGrid) return inner;

  const responsiveSize = size !== null && typeof size === "object";

  // Inside a <FormGrid>, place the field as a CSS-grid cell spanning `size`
  // columns (full width below the sm breakpoint). With no FormGrid ancestor,
  // fall back to the legacy MUI <Grid item> so older forms keep working.
  if (inFormGrid) {
    const span = (n) => `span ${Math.min(n, columns)}`;
    return (
      <Box
        sx={{
          gridColumn: responsiveSize
            ? Object.entries(size).reduce(
                (acc, [bp, n]) => ({ ...acc, [bp]: span(n) }),
                { xs: "1 / -1" },
              )
            : { xs: "1 / -1", sm: span(size) },
          minWidth: 0,
        }}
      >
        {inner}
      </Box>
    );
  }

  return (
    <Grid item xs={12} {...(responsiveSize ? size : { sm: size })}>
      {inner}
    </Grid>
  );
};

export default RffInputWrapper;
